// What the Book & pay postcode box says about what was typed in it. Pure,
// pinned by postcode-check.test.mjs (a table of every shape we know of).
//
// Robin, 9 Oct 2026: "make sure every permutation is handled, otherwise we
// should just make it a text field and not look to validate?" The rules
// (gowashmycar-research/booking-screen-trim-2026-10-09.md section 3):
//   - any case, any spacing, a hyphen, a full stop, a paste or autofill with
//     the town or "UK" round it, an O or I where the inward digit must be:
//     accepted and tidied ("N6 5UQ"), lib/postcode.js;
//   - only the district ("SW6"): asked for the rest;
//   - not a postcode at all ("12", "nowhere"): "Check the postcode";
//   - a real postcode outside London and the South East (Scotland, Jersey):
//     a soft note, never a refusal (Robin's standing ruling, W8, 5 Oct 2026:
//     "no relax the service area constraint"), flagged for support;
//   - a postcode postcodes.io does not know (a new build): a soft note,
//     never a refusal, and the order is flagged for support server-side.
import { extractPostcodeArea, isFullPostcode, normalisePostcode, SE_UK_POSTCODE_AREAS } from './postcode.js'

export const POSTCODE_MESSAGES = {
  missing: 'Add the postcode, e.g. N6 5UQ',
  partial: 'Add the full postcode, e.g. N6 5UQ',
  not_a_postcode: 'Check the postcode, e.g. N6 5UQ',
  out_of_area_soft: "We don't usually cover that postcode. Book anyway and we'll confirm with you.",
  not_found_soft: "We couldn't find that postcode. Check it, or carry on if it's a new build."
}

const squash = text => String(text || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
const OUTWARD_ONLY = /^[A-Z]{1,2}\d[A-Z\d]?$/

// null when the box holds a whole postcode; otherwise { reason, message }
// with reason 'missing' | 'partial' | 'not_a_postcode'. Only missing data
// stops a booking: where the postcode is never does.
export const postcodeProblem = text => {
  const typed = String(text || '').trim()
  if (!typed) return { reason: 'missing', message: POSTCODE_MESSAGES.missing }
  if (!isFullPostcode(typed)) {
    const reason = OUTWARD_ONLY.test(squash(typed)) ? 'partial' : 'not_a_postcode'
    return { reason, message: POSTCODE_MESSAGES[reason] }
  }
  return null
}

// A whole postcode outside London and the South East (SE_UK_POSTCODE_AREAS).
export const outsideUsualArea = text =>
  isFullPostcode(text) && !SE_UK_POSTCODE_AREAS.has(extractPostcodeArea(normalisePostcode(text)))

// The soft note under a whole postcode, or null: outside the usual area
// first, then one postcodes.io answered 404 for. Neither stops the booking.
export const postcodeNote = (text, status) => {
  if (postcodeProblem(text)) return null
  if (outsideUsualArea(text)) return POSTCODE_MESSAGES.out_of_area_soft
  if (status === 404) return POSTCODE_MESSAGES.not_found_soft
  return null
}

// The outward code ("SW6"), the only part of a postcode an event may carry.
export const postcodeOutward = text => {
  if (!isFullPostcode(text)) {
    const s = squash(text)
    return OUTWARD_ONLY.test(s) ? s : null
  }
  return normalisePostcode(text).split(' ')[0] || null
}

// postcode_checked's result from what the box holds and what postcodes.io
// answered (an HTTP status, or null when it was not asked or did not
// answer): 'ok' | 'not_found_soft' | 'out_of_area_soft' | 'bad_format' |
// 'unknown'.
export const postcodeCheckResult = (text, status) => {
  if (postcodeProblem(text)) return 'bad_format'
  if (outsideUsualArea(text)) return 'out_of_area_soft'
  if (status === 200) return 'ok'
  if (status === 404) return 'not_found_soft'
  return 'unknown'
}
